// Every other place the Book uses a word the Author just changed, each one a
// suggestion they keep or leave. The dialog says what accepting costs before
// anything happens: an Approved Chapter goes back to Review, a Rendered one's
// audio goes stale, and under the "exceptions" policy a Style Sheet change
// takes every approval with it. Nothing is written until Apply.

import type {
  BookDetail,
  ChapterSummary,
  WordChangeApplyRequest,
  WordChangeChapter,
  WordChangeSuggestion,
  WordChangeSuggestions,
} from "@contract";
import { isEndingChange } from "@contract";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/control/Button";
import { StateChip } from "../../../components/control/StateChip";
import { Banner } from "../../../components/feedback/Banner";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { Dialog } from "../../../components/overlay/Dialog";
import { suggestionKey } from "./useWordChange";

/** What Apply hands back: edits for the open draft, and the request for everything else. */
export interface WordChangeSelection {
  /** Accepted places in the Chapter being edited, per Block. */
  readonly draft: ReadonlyMap<string, readonly WordChangeSuggestion[]>;
  /** Null when nothing outside the Chapter being edited was accepted. */
  readonly request: WordChangeApplyRequest | null;
}

const selectable = (chapter: WordChangeChapter) => chapter.blocked === null;

function initialSelection(suggestions: WordChangeSuggestions): Set<string> {
  const keys = new Set<string>();
  // A changed ending is grammar for one sentence; the Author picks, if anything.
  if (isEndingChange(suggestions.change)) return keys;
  for (const chapter of suggestions.chapters)
    if (selectable(chapter))
      for (const s of chapter.suggestions)
        keys.add(suggestionKey(chapter.chapterId, s.blockId, s.offset));
  return keys;
}

export function selectionOf(
  suggestions: WordChangeSuggestions,
  selected: ReadonlySet<string>,
  styleSheet: boolean,
): WordChangeSelection {
  const draft = new Map<string, WordChangeSuggestion[]>();
  const chapters: WordChangeApplyRequest["chapters"][number][] = [];
  for (const chapter of suggestions.chapters) {
    if (!selectable(chapter)) continue;
    const accepted = chapter.suggestions.filter((s) =>
      selected.has(suggestionKey(chapter.chapterId, s.blockId, s.offset)),
    );
    if (!accepted.length) continue;
    if (chapter.isSource) {
      for (const s of accepted) draft.set(s.blockId, [...(draft.get(s.blockId) ?? []), s]);
      continue;
    }
    chapters.push({
      chapterId: chapter.chapterId,
      versionId: chapter.versionId,
      occurrences: accepted.map(({ blockId, offset, found }) => ({ blockId, offset, found })),
    });
  }
  const sheet = suggestions.styleSheet;
  const withSheet = styleSheet && sheet !== null && sheet.blocked === null;
  // Every form the model accepted, from the scripts and from the entry itself.
  const forms = [
    ...new Set([
      ...suggestions.chapters.flatMap((c) =>
        c.suggestions.filter((s) => s.kind === "form").map((s) => s.found),
      ),
      ...(sheet?.forms ?? []),
    ]),
  ];
  return {
    draft,
    request:
      chapters.length || withSheet
        ? {
            change: suggestions.change,
            chapters,
            styleSheet: withSheet
              ? { baseVersion: sheet.baseVersion, termIndex: sheet.termIndex, forms }
              : null,
          }
        : null,
  };
}

export function WordChangeDialog({
  open,
  suggestions,
  book,
  busy,
  error,
  onApply,
  onClose,
}: {
  open: boolean;
  suggestions: WordChangeSuggestions;
  book: BookDetail;
  busy: boolean;
  error: unknown;
  onApply: (selection: WordChangeSelection) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("review");
  const [selected, setSelected] = useState(() => initialSelection(suggestions));
  const endingOnly = isEndingChange(suggestions.change);
  const [styleSheet, setStyleSheet] = useState(
    () => suggestions.styleSheet?.blocked === null && !endingOnly,
  );
  const summaries = useMemo(
    () => new Map(book.chapters.map((chapter) => [chapter.id as string, chapter])),
    [book.chapters],
  );

  const toggle = (keys: readonly string[], on: boolean) =>
    setSelected((old) => {
      const next = new Set(old);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  const selection = selectionOf(suggestions, selected, styleSheet);
  const places = selected.size;
  const affected = suggestions.chapters.filter(
    (chapter) =>
      !chapter.isSource &&
      selectable(chapter) &&
      chapter.suggestions.some((s) =>
        selected.has(suggestionKey(chapter.chapterId, s.blockId, s.offset)),
      ),
  );
  const backToReview = affected.filter(
    (c) => c.state === "approved" || c.state === "rendered",
  ).length;
  const stale = affected.filter((c) => c.state === "rendered").length;
  const sheetChosen = selection.request?.styleSheet != null;
  const selectableCount = suggestions.chapters
    .filter(selectable)
    .reduce((n, chapter) => n + chapter.suggestions.length, 0);
  const nothing = places === 0 && !sheetChosen;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("wordChange.title")}
      width="lg"
      dismissable={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t("wordChange.keep")}
          </Button>
          <Button onClick={() => onApply(selection)} busy={busy} disabled={nothing}>
            {t("wordChange.apply", { count: places + (sheetChosen ? 1 : 0) })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 text-ui">
        <p className="m-0">
          {t("wordChange.intro", { from: suggestions.change.from, to: suggestions.change.to })}
        </p>
        <div className="border-line border-y py-2 text-data text-ink-2" role="status">
          <p className="m-0">{t("wordChange.summary.places", { count: places })}</p>
          {backToReview ? (
            <p className="m-0">{t("wordChange.summary.backToReview", { count: backToReview })}</p>
          ) : null}
          {stale ? <p className="m-0">{t("wordChange.summary.stale", { count: stale })}</p> : null}
          {sheetChosen && places < selectableCount ? (
            <p className="m-0">
              {t("wordChange.summary.uncovered", { from: suggestions.change.from })}
            </p>
          ) : null}
          {sheetChosen && book.reviewPolicy === "exceptions" ? (
            <p className="m-0">{t("wordChange.summary.exceptions")}</p>
          ) : null}
        </div>
        {endingOnly ? <p className="m-0 text-ink-2">{t("wordChange.endingOnly")}</p> : null}
        {suggestions.formsChecked ? null : (
          <Banner tone="warn">{t("wordChange.formsUnchecked")}</Banner>
        )}
        {suggestions.styleSheet ? (
          <section
            className="border border-line p-3"
            aria-label={t("wordChange.styleSheet.heading")}
          >
            <p className="label-mono m-0 mb-2 text-ink-3">{t("wordChange.styleSheet.heading")}</p>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 accent-accent"
                checked={styleSheet && suggestions.styleSheet.blocked === null}
                disabled={suggestions.styleSheet.blocked !== null}
                onChange={(event) => setStyleSheet(event.target.checked)}
              />
              <span>
                {t("wordChange.styleSheet.rename", { term: suggestions.styleSheet.term })}
                <span className="block font-mono text-data text-ink-2">
                  {suggestions.styleSheet.renames
                    .map((r) => `${r.found} → ${r.replacement}`)
                    .join(" · ")}
                </span>
                <span className="block text-ink-2">
                  {suggestions.styleSheet.blocked
                    ? t(`wordChange.styleSheet.blocked.${suggestions.styleSheet.blocked}`)
                    : t("wordChange.styleSheet.keeps", {
                        sayAs: suggestions.styleSheet.ipa
                          ? `/${suggestions.styleSheet.ipa}/`
                          : suggestions.styleSheet.say_as,
                      })}{" "}
                  {suggestions.styleSheet.blocked ? null : t("wordChange.styleSheet.why")}
                </span>
              </span>
            </label>
          </section>
        ) : null}
        {suggestions.chapters.map((chapter) => (
          <ChapterGroup
            key={chapter.chapterId}
            chapter={chapter}
            summary={summaries.get(chapter.chapterId)}
            selected={selected}
            onToggle={toggle}
          />
        ))}
        {error ? <ErrorBanner error={error} /> : null}
      </div>
    </Dialog>
  );
}

function ChapterGroup({
  chapter,
  summary,
  selected,
  onToggle,
}: {
  chapter: WordChangeChapter;
  summary: ChapterSummary | undefined;
  selected: ReadonlySet<string>;
  onToggle: (keys: readonly string[], on: boolean) => void;
}) {
  const { t } = useTranslation("review");
  const keys = chapter.suggestions.map((s) =>
    suggestionKey(chapter.chapterId, s.blockId, s.offset),
  );
  const chosen = keys.filter((key) => selected.has(key)).length;
  const heading = chapter.isSource
    ? t("wordChange.thisChapter")
    : t("wordChange.chapterHeading", {
        number: summary?.number ?? "–",
        title: summary?.title ?? "",
      });
  const consequence =
    chapter.isSource || chapter.blocked
      ? null
      : chapter.state === "rendered"
        ? t("wordChange.consequence.rendered")
        : chapter.state === "approved"
          ? t("wordChange.consequence.approved")
          : null;
  return (
    <section aria-label={heading} className="flex flex-col gap-1">
      <p className="m-0 flex flex-wrap items-baseline gap-3">
        {/* One checkbox for the whole Chapter: a name can occur a hundred times. */}
        <label className="flex items-baseline gap-2 font-medium">
          <input
            type="checkbox"
            className="accent-accent"
            checked={chapter.blocked === null && chosen === keys.length}
            ref={(input) => {
              if (input) input.indeterminate = chosen > 0 && chosen < keys.length;
            }}
            disabled={chapter.blocked !== null}
            onChange={() => onToggle(keys, chosen < keys.length)}
          />
          {heading}
        </label>
        {chapter.isSource ? null : <StateChip kind="chapter" state={chapter.state} />}
        {consequence ? <span className="text-data text-warn">{consequence}</span> : null}
        {chapter.blocked ? (
          <span className="text-data text-ink-2">{t(`wordChange.blocked.${chapter.blocked}`)}</span>
        ) : null}
      </p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {chapter.suggestions.map((s) => {
          const key = suggestionKey(chapter.chapterId, s.blockId, s.offset);
          return (
            <li key={key}>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 accent-accent"
                  checked={chapter.blocked === null && selected.has(key)}
                  disabled={chapter.blocked !== null}
                  onChange={(event) => onToggle([key], event.target.checked)}
                />
                <span className="font-serif">
                  {s.snippet.clippedStart ? "… " : ""}
                  {s.snippet.before}
                  <del className="bg-diff-del">
                    <span className="sr-only">{t("wordChange.removed")}: </span>
                    {s.found}
                  </del>
                  <ins className="ml-0.5 bg-diff-ins no-underline">
                    <span className="sr-only">{t("wordChange.inserted")}: </span>
                    {s.replacement}
                  </ins>
                  {s.snippet.after}
                  {s.snippet.clippedEnd ? " …" : ""}
                  {s.kind === "form" ? (
                    <span className="ml-2 font-mono text-label text-ink-3">
                      {t("wordChange.form")}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
